import { MODULE_PROMPTS, TABS } from "./modules";

/**
 * How much each piece of locked work depends on *who she serves*.
 *
 * When a coach changes her niche, this map decides what she's told about the
 * work she's already locked. The whole point is proportion: a flat list of
 * everything that touched the old niche reads as "start over", which isn't true.
 * Most of what she built is about *her* and survives untouched.
 *
 * Tiers:
 *   "redo"  — built around the old client. Needs a real rebuild.
 *   "check" — niche-flavoured. Usually an edit, not a regeneration.
 *   "safe"  — about her, not her client. Never flagged.
 *
 * A module absent from this map is never flagged. That covers Ask Brill and the
 * Ideation tools, which hold nothing that can go stale, and — deliberately, as of
 * 2026-08-19 — the Momentum modules (Website, Content Plan, Course). Momentum is
 * downstream of the foundation and is regenerated from it, so flagging it during
 * a pivot was noise while the foundation itself was still being rebuilt. Adding
 * it back is three lines here.
 *
 * ── Sections carry the tier; modules carry the worst one ────────────────────
 *
 * The tier lives on the section, but nothing is ever *presented* per section.
 * findStaleModules groups by module and gives each module the worst tier inside
 * it, and that separation is what the screen needs, because both halves are true
 * at once:
 *
 * 1. Real modules are mixed. My Visibility is the clearest case — where she shows
 *    up and how she sells survive a pivot, but her content pillars are about the
 *    client and change with them. Flattening that to one colour per module either
 *    hides a rebuild or invents four.
 * 2. A flat list of sections is still the wrong thing to show her. "11 need real
 *    work, 7 just a look" across five modules is arithmetic, not a plan — and it
 *    split single modules across two lists, which is what had her working a module
 *    from two places at once.
 *
 * What is emphatically NOT tiered per section is the rebuild itself. A module is
 * walked whole, from the top, in MODULE_PROMPTS order (see rebuildQueueFor) —
 * skipping the amber ones would put her back in the middle of a module, which is
 * the thing this was meant to fix.
 *
 * Module keys are stored in outputs.module_key and can't drift. Section names are
 * matched against MODULE_PROMPTS by string, so a renamed prompt silently falls
 * back to `_default`; keep the two in step.
 */

export const TIER = {
  REDO: "redo",
  CHECK: "check",
  SAFE: "safe",
};

export const NICHE_SENSITIVITY = {
  // ── Built around the old client. ─────────────────────────────────────────
  // The niche originates in My Mission, and My Client is the most
  // niche-dependent thing in Brill. My Market is the expensive one — a new ICP
  // means new research and new discovery calls with real people, and the review
  // copy says so out loud.
  mission:     { _default: TIER.REDO },
  client:      { _default: TIER.REDO },
  market:      { _default: TIER.REDO },
  positioning: { _default: TIER.REDO },

  offer: {
    _default: TIER.REDO,
    // The two that are about how she works rather than who she works with. She
    // still reads them — a new client can change the words even when the
    // substance holds — but they aren't sold to her as a rebuild.
    "Secret Sauce": TIER.CHECK,
    "Your Method": TIER.CHECK,
  },

  // ── Niche-flavoured rather than niche-built. Usually an edit. ────────────
  // Her story is her story: a new niche changes how she frames it, not what
  // happened.
  story: { _default: TIER.CHECK },

  social: {
    _default: TIER.CHECK,
    // Where she shows up and how she sells survive a pivot. What she talks
    // about, and who she talks to, does not — content pillars are the one thing
    // in here that is straightforwardly about the client.
    "Content Pillars": TIER.REDO,
  },

  // ── About her. A niche pivot doesn't touch these. ────────────────────────
  vision: { _default: TIER.SAFE },
  voice:  { _default: TIER.SAFE },
};

/**
 * The tier for one section. Unmapped modules return null (never flagged).
 *
 * `_default` covers every section in a module, including ones added later and
 * dynamically-named ones. Named sections override it, and section names have to
 * match MODULE_PROMPTS exactly — they're the keys the `outputs` table stores, so
 * a renamed prompt silently falls back to `_default`.
 */
export function tierFor(moduleKey, sectionName) {
  const mod = NICHE_SENSITIVITY[moduleKey];
  if (!mod) return null;
  return (sectionName && mod[sectionName]) || mod._default || null;
}

/** True for the two tiers that actually surface to the coach. */
const flags = (tier) => tier === TIER.REDO || tier === TIER.CHECK;

/**
 * Would a stale section here actually show her a "!"?
 *
 * The distinction that `isStale` cannot make. A stamp older than her active
 * niche means "built under a different niche"; whether that MATTERS is what this
 * map decides. My Vision is SAFE — "about her, a pivot doesn't touch these" — so
 * its sections carry an old stamp and no mark, on purpose.
 *
 * Her rule, 2026-09-01: "if Me had an ! that would make sense, but if you said
 * it was good then it should count it as done." Anything offering her work to do
 * has to ask THIS question, not the stamp question, or it offers to restart a
 * section the app has already decided is fine.
 */
export function flagsToCoach(moduleKey, sectionName) {
  return flags(tierFor(moduleKey, sectionName));
}

/**
 * Every section of a module, in the order Brill works through it.
 *
 * MODULE_PROMPTS is the running order, so it leads. Anything locked that isn't
 * in that list — a section renamed since she locked it, or one from a module
 * with no fixed list — is appended rather than dropped, because a section we
 * can't place is still a section she built on the old niche.
 */
function sectionsFor(moduleKey, stamped) {
  const order = MODULE_PROMPTS[moduleKey] || [];
  const locked = Object.keys(stamped || {});
  return [...order, ...locked.filter((name) => !order.includes(name))];
}

/**
 * Which modules hold work built on a niche other than the active one — one entry
 * per affected module, grouped and ordered the way the review screen shows them.
 *
 *   { moduleKey, tier, stale: [{ name, tier }], lockedCount, redoCount, checkCount }
 *
 * A module can be mixed, and usually is. My Visibility is the clearest case: where
 * she shows up and how she sells survive a pivot, but her content pillars are
 * straightforwardly about the client. So sections keep their own tier, and the
 * module takes the *worst* one inside it — under-warning costs her a rebuild she
 * needed, over-warning costs a glance.
 */
export function findStaleModules(stamps, activeNicheId) {
  if (!activeNicheId || !stamps) return [];

  return TABS.map((moduleKey) => {
    if (!NICHE_SENSITIVITY[moduleKey]) return null;

    const stamped = stamps[moduleKey] || {};
    const locked = sectionsFor(moduleKey, stamped).filter((name) => name in stamped);
    const stale = locked
      .filter((name) => stamped[name] !== activeNicheId)
      .map((name) => ({ name, tier: tierFor(moduleKey, name) }))
      .filter((s) => flags(s.tier));
    if (!stale.length) return null;

    const redoCount = stale.filter((s) => s.tier === TIER.REDO).length;
    return {
      moduleKey,
      tier: redoCount > 0 ? TIER.REDO : TIER.CHECK,
      stale,
      lockedCount: locked.length,
      redoCount,
      checkCount: stale.length - redoCount,
    };
  }).filter(Boolean);
}

/**
 * The running order for rebuilding a module after a pivot.
 *
 * Every section of the module, from the top, minus the ones she has explicitly
 * cleared. It deliberately keeps sections that are already locked and already on
 * the current niche: silently stepping over those is what made the chat feel like
 * it was jumping, so the walkthrough stops at each one and offers to revise it
 * instead. The only thing that gets passed over without a word is a section she
 * clicked "Still true" on, because that was her decision and repeating it back to
 * her is just friction.
 *
 * `cleared` is a Set (or array) of section names she has confirmed this run.
 */
export function rebuildQueueFor(moduleKey, stamps, cleared = []) {
  const skip = cleared instanceof Set ? cleared : new Set(cleared);
  return sectionsFor(moduleKey, stamps?.[moduleKey]).filter((name) => !skip.has(name));
}

/**
 * The sections that make up the niche itself — what we snapshot onto the
 * outgoing version so a later redo can name what changed.
 */
export const NICHE_SNAPSHOT_SECTIONS = {
  mission: ["The One Problem", "Who You're For", "The Transformation Promise", "Niche Statement"],
  client: ["Ideal Client Profile", "Tension & Transformation"],
};

/** Flatten the niche-defining sections out of moduleOutputs into one object. */
export function buildNicheSnapshot(moduleOutputs) {
  const snapshot = {};
  Object.entries(NICHE_SNAPSHOT_SECTIONS).forEach(([moduleKey, names]) => {
    names.forEach((name) => {
      const value = moduleOutputs?.[moduleKey]?.[name];
      if (value) snapshot[name] = value;
    });
  });
  return snapshot;
}
